import React, { useEffect, useState } from 'react';

import { Box, Col, Row, Text } from '@opendoor/bricks-next';
import { CtaInput } from '@opendoor/bricks/complex';
import { InputProps } from '@opendoor/bricks/core';
import { analytics } from '@opendoor/observability/slim';

import * as api from '../api';
import { useObservability } from '../../helpers/observability';
import { PARTNERSHIP_BFF_URL } from '../globals';

const valuePropItems = [
  {
    header: 'Streamline your process',
    description:
      'Create a custom profile to track and manage every touch point of your customer\u2019s journey.',
  },
  {
    header: 'Help your client get started',
    description:
      'Help your client get started. Use the Home Eligibility Checker to confirm your client\u2019s home is eligible.',
  },
  {
    header: 'Sell more homes, faster',
    description:
      'Get your clients a competitive estimated offer within minutes \u2014 all with a few simple clicks.',
  },
  {
    header: 'Access dedicated support',
    description:
      'Access your own partner manager, training, insights, and more to help you sell more homes.',
  },
];

const BuilderSalesPage: React.FC = () => {
  const { trackEvent } = useObservability();
  const [email, setEmail] = useState<string>();
  const [ctaInputLoading, setCtaInputLoading] = useState(false);
  const [isEmailInvalid, setIsEmailInvalid] = useState<boolean>(false);

  useEffect(() => {
    analytics.trackEvent('partner-sign-up-flow', 'view-landing-page');
  }, []);

  const onEmailChange: InputProps['onChange'] = (_event, value) => {
    setEmail(value);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLElement>) => {
    event.preventDefault();
    if (!email) {
      return;
    }
    setCtaInputLoading(true);
    try {
      const resp = await api.validatePartnerEmailAddress(email);
      if (!resp || !resp.validateEmail.isValid) {
        setIsEmailInvalid(true);
        setCtaInputLoading(false);
        trackEvent(
          'invalid-email',
          undefined,
          { email },
          { categoryOverride: 'partner-sign-up-flow' },
        );
        return;
      }
      trackEvent('valid-email', undefined, undefined, {
        categoryOverride: 'partner-sign-up-flow',
      });
      const normalizedEmail = resp.validateEmail.normalizedEmail;
      setEmail(normalizedEmail);
      const redirectParams = new URLSearchParams();
      redirectParams.set('email', normalizedEmail);
      redirectParams.set('partnershipType', 'HOME_BUILDER');
      redirectParams.set('role', 'partner_sales_referrer');
      window.location.assign(`${PARTNERSHIP_BFF_URL}/sign-up?${redirectParams.toString()}`);
    } catch {
      setCtaInputLoading(false);
    }
  };

  return (
    <Col
      p="$16"
      gap="$2"
      backgroundColor="$backgroundAccentBrand"
      $largerThanMD={{ height: '$150' }}
    >
      <Col alignItems="flex-start" mx="auto" $largerThanXS={{ px: '$0' }} p="$2" maxWidth="$232">
        <Text
          tag="h1"
          typography="$headerSmall"
          mb="$3half"
          color="$contentInversePrimary"
          textAlign="left"
          $largerThanSM={{ maxWidth: '50%' }}
          ml="$0"
        >
          Sign up for your customized dashboard today
        </Text>
        <Box width="100%" height="$8" marginTop="$1">
          <CtaInput
            designStyle="roundedSquare"
            placeholderText="Enter your work email"
            inputType="email"
            onChange={onEmailChange}
            value={email}
            unboundedWidth={true}
            onSubmit={handleSubmit}
            paddingLevel="light"
            ctaProps={{
              onClick: () => {},
              actionText: 'Get started',
              'aria-label': 'Get started',
              loading: ctaInputLoading,
            }}
          />
          {isEmailInvalid && (
            <Text
              tag="p"
              backgroundColor="$backgroundStateInactiveAccentError"
              typography="$labelSmall"
              pl="$2"
              py="$2"
              color="$contentInversePrimary"
              textAlign="left"
              borderRadius="$4x"
              mt="$1"
            >
              Please use an email address associated with your builder to continue.
            </Text>
          )}
        </Box>

        <Row mt="$22" flexWrap="wrap" width="100%" gap="$4" $largerThanSM={{ flexWrap: 'nowrap' }}>
          {valuePropItems.map((valueProp, i) => (
            <Col key={i} width="100%" $largerThanSM={{ width: '25%' }} gap="$2">
              <Text tag="h4" typography="$subheaderXsmall" color="$contentInversePrimary">
                {valueProp.header}
              </Text>
              <Text tag="p" color="$contentInversePrimary" typography="$bodySmall" width="85%">
                {valueProp.description}
              </Text>
            </Col>
          ))}
        </Row>
      </Col>
    </Col>
  );
};

export default BuilderSalesPage;
